import { useMemo, useState } from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  CrownOutlined,
  DashboardOutlined,
  GiftOutlined,
  LockOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ResponsiveAppShell } from '@mall/shared';
import { useAuth } from '../features/auth/AuthProvider';
import { useToast } from './ToastProvider';
import { ChangePasswordModal } from '../features/auth/ChangePasswordModal';

const MENU_ITEMS = [
  { key: '/', icon: <DashboardOutlined />, label: '概览' },
  { key: '/tenants', icon: <ApartmentOutlined />, label: '租户' },
  { key: '/categories', icon: <AppstoreOutlined />, label: '全局分类' },
  { key: '/products', icon: <GiftOutlined />, label: '商品管理' },
  { key: '/sync', icon: <SyncOutlined />, label: '商品同步' },
];

type PasswordModalKind = 'login' | 'second';

export function OperatorShell() {
  const { session, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [passwordModal, setPasswordModal] = useState<{
    open: boolean;
    kind: PasswordModalKind;
  }>({ open: false, kind: 'login' });

  const selectedKey = useMemo(() => {
    if (location.pathname.startsWith('/tenants')) return '/tenants';
    if (location.pathname.startsWith('/categories')) return '/categories';
    if (location.pathname.startsWith('/products')) return '/products';
    if (location.pathname.startsWith('/sync')) return '/sync';
    return '/';
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('已退出登录');
    navigate('/login', { replace: true });
  };

  return (
    <ResponsiveAppShell
      brand={{
        mark: <CrownOutlined />,
        title: 'Mall Operator',
        subtitle: '跨租户运营与治理',
      }}
      menuItems={MENU_ITEMS}
      selectedKey={selectedKey}
      onNavigate={(key) => navigate(key)}
      topbarTitle="跨租户运营平台"
      topbarSubtitle="租户、全局分类与商品价格治理"
      contextPill={<span className="mall-context-pill">运营控制台</span>}
      userLabel={session?.username ?? '未登录'}
      userMenu={{
        items: [
          {
            key: 'pwd-login',
            icon: <LockOutlined />,
            label: '修改登录密码',
            onClick: () => setPasswordModal({ open: true, kind: 'login' }),
          },
          {
            key: 'pwd-second',
            icon: <SafetyCertificateOutlined />,
            label: '修改二次密码',
            onClick: () => setPasswordModal({ open: true, kind: 'second' }),
          },
          {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '退出登录',
            onClick: () => void handleLogout(),
          },
        ],
      }}
      extras={
        <ChangePasswordModal
          open={passwordModal.open}
          kind={passwordModal.kind}
          onClose={() => setPasswordModal((m) => ({ ...m, open: false }))}
        />
      }
    >
      <Outlet />
    </ResponsiveAppShell>
  );
}
